import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FeedbackResultDoc } from '@/dao/firestore/FeedbackResultsRepository';
import { FeedbackSummaryDoc } from '@/dao/firestore/FeedbackSummaryRepository';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { PieChart, Pie, Cell, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip, BarChart, Bar, XAxis, YAxis, ReferenceLine } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Heart, TrendingUp, TrendingDown, AlertCircle, CheckCircle } from 'lucide-react';

interface AnalyticsModalProps {
  open: boolean;
  onClose: () => void;
  results: FeedbackResultDoc[];
  summary?: FeedbackSummaryDoc | null;
}

export function AnalyticsModal({ 
  open, 
  onClose, 
  results,
  summary
}: AnalyticsModalProps) {
  const { t } = useTranslation();

  // Helper function to format time in mm:ss
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Sentiment analysis timeline data
  const sentimentTimelineData = summary?.sense_analysis?.map((block, index) => ({
    block: index + 1,
    score: block.score,
    timeLabel: `${formatTime(block.start)}-${formatTime(block.end)}`,
    summary: block.summary,
    start: block.start,
    end: block.end
  })) || [];

  // Sentiment insights calculations
  const sentimentAnalysis = summary?.sense_analysis || [];
  const averageScore = sentimentAnalysis.length > 0 
    ? sentimentAnalysis.reduce((sum, block) => sum + block.score, 0) / sentimentAnalysis.length 
    : 0;
  
  const highestBlock = sentimentAnalysis.reduce((max, block, index) => 
    block.score > (sentimentAnalysis[max]?.score || -Infinity) ? index : max, 0);
  
  const lowestBlock = sentimentAnalysis.reduce((min, block, index) => 
    block.score < (sentimentAnalysis[min]?.score || Infinity) ? index : min, 0);

  const positiveBlocks = sentimentAnalysis.filter(block => block.score > 0).length;
  const negativeBlocks = sentimentAnalysis.filter(block => block.score < 0).length;
  const neutralBlocks = sentimentAnalysis.filter(block => block.score === 0).length;

  // Sentiment trend (improvement/decline)
  const sentimentTrend = sentimentAnalysis.length > 1 
    ? sentimentAnalysis[sentimentAnalysis.length - 1].score - sentimentAnalysis[0].score 
    : 0;

  // Sentiment distribution
  const sentimentData = [
    { name: t('screeningRoom.analytics.positive'), value: results.filter(r => r.sentiment === 'positive').length, color: 'hsl(var(--chart-1))' },
    { name: t('screeningRoom.analytics.neutral'), value: results.filter(r => r.sentiment === 'neutral').length, color: 'hsl(var(--chart-2))' },
    { name: t('screeningRoom.analytics.negative'), value: results.filter(r => r.sentiment === 'negative').length, color: 'hsl(var(--chart-3))' }
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" />
            {t('screeningRoom.analytics.title')}
          </DialogTitle>
        </DialogHeader>

        {sentimentAnalysis.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8 text-muted-foreground">
              {t('screeningRoom.analytics.noData')}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Sentiment Insights Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Heart className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{t('screeningRoom.analytics.averageScore')}</span>
                  </div>
                  <div className="text-2xl font-bold">{averageScore.toFixed(1)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {averageScore > 0 ? t('screeningRoom.analytics.positive') : averageScore < 0 ? t('screeningRoom.analytics.negative') : t('screeningRoom.analytics.neutral')}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    {sentimentTrend > 0 ? 
                      <TrendingUp className="h-4 w-4 text-green-500" /> : 
                      sentimentTrend < 0 ?
                      <TrendingDown className="h-4 w-4 text-red-500" /> :
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    }
                    <span className="text-sm text-muted-foreground">{t('screeningRoom.analytics.trend')}</span>
                  </div>
                  <div className="text-2xl font-bold">
                    {sentimentTrend > 0 ? '+' : ''}{sentimentTrend.toFixed(1)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {sentimentTrend > 0 ? t('screeningRoom.analytics.improvement') : sentimentTrend < 0 ? t('screeningRoom.analytics.decline') : t('screeningRoom.analytics.stable')}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-muted-foreground">{t('screeningRoom.analytics.bestMoment')}</span>
                  </div>
                  <div className="text-2xl font-bold">
                    {formatTime(sentimentAnalysis[highestBlock]?.start || 0)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t('screeningRoom.analytics.score')}: {sentimentAnalysis[highestBlock]?.score.toFixed(1)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-muted-foreground">{t('screeningRoom.analytics.criticalMoment')}</span>
                  </div>
                  <div className="text-2xl font-bold">
                    {formatTime(sentimentAnalysis[lowestBlock]?.start || 0)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t('screeningRoom.analytics.score')}: {sentimentAnalysis[lowestBlock]?.score.toFixed(1)}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sentiment Timeline Chart */}
            <Card className="col-span-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5" />
                  {t('screeningRoom.analytics.sentimentTimelineTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sentimentTimelineData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                      <XAxis 
                        dataKey="timeLabel" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval={0}
                        fontSize={12}
                      />
                      <YAxis 
                        domain={[-5, 5]}
                        tickFormatter={(value) => value.toString()}
                      />
                      <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload[0]) {
                            const data = payload[0].payload;
                            return (
                               <div className="bg-background border rounded-lg p-3 shadow-lg max-w-xs">
                                 <p className="font-semibold">{t('common.blockLabel')} {data.block}</p>
                                 <p className="text-sm text-muted-foreground">{data.timeLabel}</p>
                                 <p className="text-sm mt-1">{t('common.scoreLabel')}: {data.score}</p>
                                <p className="text-xs mt-2 text-muted-foreground">{data.summary}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar 
                        dataKey="score" 
                        radius={[2, 2, 0, 0]}
                      >
                        {sentimentTimelineData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.score >= 0 ? 'hsl(var(--chart-1))' : 'hsl(var(--chart-3))'} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Sentiment Insights Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Key Insights */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('screeningRoom.analytics.keyMoments')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Best moment */}
                  <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-800 dark:text-green-200">
                        {t('screeningRoom.analytics.bestMoment')} ({formatTime(sentimentAnalysis[highestBlock]?.start || 0)})
                      </span>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {sentimentAnalysis[highestBlock]?.summary}
                    </p>
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                      {t('screeningRoom.analytics.score')}: {sentimentAnalysis[highestBlock]?.score.toFixed(1)}
                    </div>
                  </div>

                  {/* Worst moment */}
                  <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <span className="font-medium text-red-800 dark:text-red-200">
                        {t('screeningRoom.analytics.criticalMoment')} ({formatTime(sentimentAnalysis[lowestBlock]?.start || 0)})
                      </span>
                    </div>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      {sentimentAnalysis[lowestBlock]?.summary}
                    </p>
                    <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {t('screeningRoom.analytics.score')}: {sentimentAnalysis[lowestBlock]?.score.toFixed(1)}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('screeningRoom.analytics.sentimentDistribution')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('screeningRoom.analytics.positiveBlocks')}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="font-medium">{positiveBlocks}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('screeningRoom.analytics.neutralBlocks')}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                      <span className="font-medium">{neutralBlocks}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('screeningRoom.analytics.negativeBlocks')}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="font-medium">{negativeBlocks}</span>
                    </div>
                  </div>
                  
                  {/* Progress bars */}
                  <div className="space-y-2 mt-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-green-600 w-16">{t('screeningRoom.analytics.positive')}</span>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${(positiveBlocks / sentimentAnalysis.length) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {Math.round((positiveBlocks / sentimentAnalysis.length) * 100)}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-16">{t('screeningRoom.analytics.neutral')}</span>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-gray-400 h-2 rounded-full" 
                          style={{ width: `${(neutralBlocks / sentimentAnalysis.length) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {Math.round((neutralBlocks / sentimentAnalysis.length) * 100)}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-600 w-16">{t('screeningRoom.analytics.negative')}</span>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full" 
                          style={{ width: `${(negativeBlocks / sentimentAnalysis.length) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {Math.round((negativeBlocks / sentimentAnalysis.length) * 100)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}